<template>
    <div class="request-list">
        <Table :columns="tblColumns" :data="tblData" :loading="isLoading" class="request-list__table" />

        <div class="request-list__cards">
            <Spin v-if="isLoading" fix />
            <p v-if="!tblData.length" class="request-list__empty">No data</p>
            <div v-for="row in tblData" :key="row.rowKey || row._id" class="request-card">
                <div class="request-card__head">
                    <div>
                        <div class="request-card__dates">{{ row.user && row.user.name }}</div>
                        <div class="request-card__meta">
                            {{ formatDate(row.requestedStart) }} to {{ formatDate(row.requestedEnd) }}
                        </div>
                        <div class="request-card__meta">
                            {{ formatLeaveType(row.requestedData && row.requestedData.type) }} ·
                            {{ row.requestedData && row.requestedData.mode }}
                        </div>
                    </div>
                    <span :class="['request-status', `request-status--${getLeaveStatus(row)}`]">
                        <span class="request-status__dot" />{{ formatStatus(getLeaveStatus(row)) }}
                    </span>
                </div>
                <div class="request-card__foot">
                    <span class="request-card__filed">Created {{ formatDate(row.createdAt) }}</span>
                    <div class="request-actions">
                        <Button
                            v-for="btn in actionsFor(row)"
                            :key="btn.content"
                            size="small"
                            :icon="btn.icon"
                            :class="{ 'request-actions__cancel': btn.danger, 'request-actions__approve': btn.success }"
                            @click="btn.onClick"
                        >
                            {{ btn.content }}
                        </Button>
                    </div>
                </div>
            </div>
        </div>

        <Page
            :total="totalCount"
            :current="currentPage"
            :page-size="totalPerPage"
            size="small"
            class="request-list__pagination"
            @on-change="handleOnpageChanged"
        />
        <ViewRequestModal ref="ViewRequest" @create="handleReloadList" />
    </div>
</template>

<script>
import moment from 'moment';
import {
    formatLeaveType,
    expandLeaveDays,
    formatStatus,
    getLeaveStatus,
    renderActions,
    renderStatus,
} from '@/components/Requests/Dev/shared/tableRender';
import ViewRequestModal from '@/components/Requests/Dev/Leave/LeaveRequest/ViewRequestModal.vue';
export default {
    components: { ViewRequestModal },
    props: {
        isLoading: {
            type: Boolean,
            default: false,
        },
        requestsDataList: {
            type: Array,
            default: () => [],
        },
        currentPage: {
            type: Number,
            default: 0,
        },
        totalCount: {
            type: Number,
            default: 0,
        },
        totalPages: {
            type: Number,
            default: 0,
        },
        totalPerPage: {
            type: Number,
            default: 0,
        },
    },

    data() {
        return {
            tblColumns: [
                {
                    title: 'Date Created',
                    key: 'createdAt',
                    minWidth: 120,
                    render: (h, params) => {
                        return h(
                            'div',
                            params.row.createdAt ? moment(params.row.createdAt).utc().format('YYYY-MM-DD') : ''
                        );
                    },
                },
                {
                    title: 'Employee',
                    key: 'user',
                    minWidth: 180,
                    render: (h, params) => {
                        return h('div', params.row.user.name);
                    },
                },
                {
                    title: 'Start Date',
                    key: 'requestedStart',
                    //fixed: 'left',
                    minWidth: 120,
                    render: (h, params) => {
                        return params.row.requestedStart
                            ? h('div', `${moment(params.row.requestedStart).utc().format('yyyy-MM-DD')}`)
                            : h('div', { class: 'hyphenFont' }, '-');
                    },
                },
                {
                    title: 'End Date',
                    key: 'requestedEnd',
                    minWidth: 120,
                    render: (h, params) => {
                        return params.row.requestedEnd
                            ? h('div', `${moment(params.row.requestedEnd).utc().format('yyyy-MM-DD')}`)
                            : h('div', { class: 'hyphenFont' }, '-');
                    },
                },
                {
                    title: 'Leave Type',
                    key: 'requestedData.type',
                    minWidth: 140,
                    render: (h, params) => {
                        return h('div', formatLeaveType(params.row.requestedData.type));
                    },
                },
                {
                    title: 'Mode',
                    key: 'type',
                    minWidth: 90,
                    render: (h, params) => {
                        return h('div', params.row.requestedData.mode);
                    },
                },
                {
                    title: 'Status',
                    key: 'status',
                    minWidth: 120,
                    render: (h, params) => renderStatus(h, getLeaveStatus(params.row)),
                },
                {
                    title: 'Actions',
                    width: 160,
                    align: 'left',
                    render: (h, params) => renderActions(h, this.actionsFor(params.row)),
                },
            ],
            tblData: [],
        };
    },

    watch: {
        requestsDataList(data) {
            this.tblData = expandLeaveDays(data);
        },
        totalPerPage(total) {
            const arrTotal = [];
            arrTotal.push(total);
        },
    },
    created() {
        if (this.requestsDataList) {
            this.tblData = expandLeaveDays(this.requestsDataList);
        }
    },
    methods: {
        formatStatus,
        formatLeaveType,
        getLeaveStatus,
        formatDate(date) {
            return date ? moment(date).utc().format('YYYY-MM-DD') : '-';
        },
        // Same buttons and visibility rules as before, just restyled
        actionsFor(row) {
            const isPending = row.status && row.status.includes('pending');
            return [
                { content: 'View', icon: 'md-eye', onClick: () => this.$refs.ViewRequest.openModalForAdmin(row) },
                ...(isPending
                    ? [
                          {
                              content: 'Approve',
                              icon: 'md-checkmark',
                              success: true,
                              onClick: () => this.handleProcessRequest(row._id, 'approved'),
                          },
                          {
                              content: 'Reject',
                              icon: 'md-close',
                              danger: true,
                              onClick: () => this.handleProcessRequest(row._id, 'rejected'),
                          },
                      ]
                    : []),
                ...(row.status && ['approved'].includes(row.status)
                    ? [
                          {
                              content: 'Cancel',
                              icon: 'ios-remove-circle-outline',
                              danger: true,
                              onClick: () => this.handleProcessRequest(row._id, 'cancel', row.leaveDate),
                          },
                      ]
                    : []),
            ];
        },
        handleReloadList() {
            this.$emit('reload');
        },
        handleOnpageChanged(page) {
            this.$emit('on-page-change', page);
        },
        // leaveDate is set on the single-day rows of an approved leave
        handleProcessRequest(id, status, leaveDate) {
            console.log('handleProcessRequest status: ', status);
            this.$emit('on-process-request', id, status, leaveDate);
        },
    },
};
</script>

<style lang="scss">
ul.pagination.ivu-page {
    text-align: end;
}
.adminRequestsList button {
    width: 25px;
    margin-left: 10px;
}
.adminRequestsList i.ivu-icon {
    margin-left: -12.5px;
}
.btn-hide {
    display: none;
}
.statusLabel {
    display: inline-block;
    color: #fff;
    border-radius: 5px;
    box-shadow: 0 0 5px -1px rgba(0, 0, 0, 0.2);
    vertical-align: middle;
    max-width: 250px;
    padding: 5px;
    text-align: center;
}
.success {
    background: #49e97e;
    font-weight: bold;
}
.warning {
    background: #ffc107;
    font-weight: bold;
}
.danger {
    background: #e9495d;
    font-weight: bold;
}
.primary {
    background: #211841;
}
.secondary {
    background: #828899;
}
</style>

<style lang="scss" scoped>
@import '../../../Dev/shared/request-table.scss';
</style>
